import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MeetingNoteCard } from "@/components/project-detail/meeting-notes/MeetingNoteCard";
import { useMeetingNote } from "@/hooks/useMeetingNotes";
import { useSetCurrentEntity } from "@/contexts/RightSidebarContext";
import { usePageHeader } from "@/hooks/usePageHeader";
import { QueryResult } from "@/components/QueryResult";
import { formatProjectName } from "@/lib/format-project-name";

export default function MeetingNoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startEditing = searchParams.get("edit") === "1";
  const { data: note, isLoading, isError, error } = useMeetingNote(id);
  useSetCurrentEntity("meeting_note", id, note?.title);
  usePageHeader({ title: note?.title });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!note) {
    return (
      <QueryResult
        isError={isError}
        error={error}
        data={note}
        entityName="meeting note"
        backTo="/meeting-notes"
        backLabel="Back to Meeting Notes"
      >
        {null}
      </QueryResult>
    );
  }

  const contextLink = note.project
    ? {
        href: `/projects/${note.project.id}`,
        label: formatProjectName(
          note.project.name,
          note.project.event_start_date,
        ),
      }
    : note.project_group
      ? {
          href: `/projects/groups/${note.project_group.id}`,
          label: note.project_group.name,
        }
      : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/meeting-notes")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          All Notes
        </Button>
        {contextLink && (
          <Link
            to={contextLink.href}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" />
            {contextLink.label}
          </Link>
        )}
      </div>

      <MeetingNoteCard
        note={note}
        initialEditing={startEditing}
        onDeleted={() => navigate("/meeting-notes")}
      />
    </div>
  );
}
